import { calculateFinalAverageForSponte } from "../utils/finalGradeCalculator";
import {
  getGradesByClassroom,
  updateVisibilityByStudent,
} from "../api/autenticoAPI";
import { setFinalGrades } from "../api/sponteAPI";
import { logDebug } from "../utils/logger";

/** Limite de envios simultâneos ao Sponte por turma (evita rajadas que estouram rate limit no cliente). */
const SPONTE_FINAL_GRADES_CONCURRENCY = 2;

/**
 * @typedef {object} SendFinalGradesResult
 * @property {number} successCount
 * @property {number} errorCount
 * @property {Array<object>} failedGrades Linhas de `grade` que falharam (para retry seletivo)
 * @property {number} attemptedCount Total processado nesta execução
 */

/**
 * Lida com o envio das notas finais para o Sponte.
 * Recebe todas as dependências como parâmetros para manter este módulo
 * independente de React.
 *
 * @param {object} params
 * @param {object|null} params.gradesSubset Se definido e não vazio, envia apenas essas linhas (retry parcial).
 */
export const sendFinalGradesToSponte = async ({
  classGrades,
  gradesSubset = null,
  setLoading,
  setMessage,
  setType,
  setShow,
}) => {
  try {
    setLoading(true);
    const isPartialRetry =
      Array.isArray(gradesSubset) && gradesSubset.length > 0;
    setMessage(
      isPartialRetry
        ? `Reenviando notas pendentes (${gradesSubset.length}) ao Sponte...`
        : "Enviando notas para o sistema Sponte..."
    );
    setType("info");
    setShow(true);

    if (!classGrades || !Array.isArray(classGrades.grades)) {
      setLoading(false);
      setType("warning");
      setMessage("Nenhuma nota encontrada para envio.");
      setShow(true);
      return {
        successCount: 0,
        errorCount: 0,
        failedGrades: [],
        attemptedCount: 0,
      };
    }

    let gradesList = classGrades.grades;
    if (isPartialRetry) {
      gradesList = gradesSubset;
    }

    const invalidGrades = gradesList.filter((grade) => {
      const ac1Value = parseFloat(
        (grade.ac1 && grade.ac1.replace(",", ".")) || 0
      );
      const ac2Value = parseFloat(
        (grade.ac2 && grade.ac2.replace(",", ".")) || 0
      );

      return (
        (isNaN(ac1Value) && isNaN(ac2Value)) ||
        ac1Value > 10 ||
        ac2Value > 10
      );
    });

    if (invalidGrades.length > 0) {
      setLoading(false);
      setType("warning");
      setMessage(
        `Foram encontradas ${invalidGrades.length} notas inválidas. Verifique antes de enviar ao Sponte.`
      );
      setShow(true);
      return {
        successCount: 0,
        errorCount: 0,
        failedGrades: [],
        attemptedCount: gradesList.length,
      };
    }

    let successCount = 0;
    let errorCount = 0;
    const totalCount = gradesList.length;
    /** @type {Array<object>} */
    const failedGrades = [];

    const updateProgress = () => {
      const processed = successCount + errorCount;
      const percentage = totalCount > 0 ? Math.round((processed / totalCount) * 100) : 0;
      setMessage(
        `Processando notas: ${processed}/${totalCount} (${percentage}%) - ${successCount} sucesso, ${errorCount} falhas`
      );
      // Garantir que o toast continue visível durante o processamento
      setShow(true);
      setType("info");
    };

    let nextIndex = 0;

    async function worker() {
      while (nextIndex < gradesList.length) {
        const index = nextIndex++;
        const grade = gradesList[index];
        const mediaFinal = calculateFinalAverageForSponte(grade);

        logDebug(
          `[Sponte] Aluno ${grade.AlunoID}: MB=${grade.mb}, RM=${grade.rm}, RB=${grade.rb}, Enviando=${mediaFinal}`
        );

        try {
          await setFinalGrades(grade, mediaFinal);
          successCount++;
          updateProgress();
        } catch (error) {
          console.error(
            `Erro ao enviar notas para aluno ${grade.AlunoID}:`,
            error
          );
          failedGrades.push(grade);
          errorCount++;
          updateProgress();
        }
      }
    }

    const poolSize = Math.min(SPONTE_FINAL_GRADES_CONCURRENCY, gradesList.length);
    await Promise.all(
      Array.from({ length: poolSize }, () => worker())
    );

    setLoading(false);

    if (errorCount === 0) {
      setMessage(
        `Sistemas atualizados com sucesso! ${successCount}/${totalCount} notas enviadas.`
      );
      setType("success");
    } else if (successCount > 0) {
      setMessage(
        `Atualização parcial: ${successCount}/${totalCount} enviadas, ${errorCount} falha(s). Use o botão "Reenviar falhas" para tentar só o que não subiu.`
      );
      setType("warning");
    } else {
      setMessage(
        `Nenhuma nota foi enviada (${errorCount} falha(s)). Use o botão "Reenviar falhas" para tentar de novo só esses alunos.`
      );
      setType("error");
    }

    setShow(true);
    // Não chamar setValidate aqui: isso dispararia em cada GradeItem validateGrades()
    // (getGradesBySubject por aluno) como no fluxo "Verificar médias". A verificação
    // contra o Sponte deve ocorrer só quando o usuário clicar nesse botão (batch).

    return {
      successCount,
      errorCount,
      failedGrades,
      attemptedCount: totalCount,
    };
  } catch (error) {
    console.error("Erro geral no envio de notas:", error);
    setLoading(false);
    setType("error");
    setMessage(
      "Ocorreu um erro ao atualizar os sistemas. Favor tentar novamente em alguns minutos."
    );
    setShow(true);
    return {
      successCount: 0,
      errorCount: 0,
      failedGrades: [],
      attemptedCount: 0,
    };
  }
};

/**
 * Atualiza a visibilidade das notas em massa para todos os alunos da turma.
 */
export const updateAllVisibilityForClass = async ({
  period,
  subject,
  studentsByClass,
  classrooms,
  selectAll,
  setLoading,
  setType,
  setMessage,
  setInfoMessage,
  setShow,
  setShowMessage,
  setClassGrades,
  setUpdateStudentVisibility,
  refetchGrades, // Mantido por compatibilidade, mas não é mais obrigatório
}) => {
  if (!period || !subject) {
    setType("warning");
    setMessage(
      "Selecione um período e uma disciplina antes de atualizar a visibilidade"
    );
    setShow(true);
    return;
  }

  if (!studentsByClass || studentsByClass.length === 0) {
    setType("warning");
    setMessage("Não há alunos para atualizar a visibilidade");
    setShow(true);
    return;
  }

  setLoading(true);
  setInfoMessage(
    `Atualizando visibilidade para todos (${
      selectAll ? "Visível" : "Não visível"
    })...`
  );
  setShowMessage(true);

  let successCount = 0;
  let errorCount = 0;

  const promises = studentsByClass.map((student) => {
    // Validar que AlunoID está presente
    if (!student.AlunoID && student.AlunoID !== 0) {
      console.error(`AlunoID inválido para estudante ${student.Nome}:`, student.AlunoID);
      errorCount++;
      return Promise.resolve(false);
    }
    
    return updateVisibilityByStudent(
      student.AlunoID,
      classrooms[1],
      subject.id,
      period,
      selectAll
    )
      .then(() => {
        successCount++;
        return true;
      })
      .catch((error) => {
        console.error(
          `Erro ao atualizar visibilidade para ${student.Nome}:`,
          error
        );
        errorCount++;
        return false;
      });
  });

  await Promise.allSettled(promises);

  // CRÍTICO: Recarregar as notas após atualizar visibilidade para refletir as mudanças
  // Isso é necessário porque a visibilidade foi atualizada no backend e precisamos
  // sincronizar o estado do frontend com os dados atualizados
  // IMPORTANTE: Sempre usar getGradesByClassroom diretamente (como na versão antiga)
  // para garantir que os dados sejam atualizados corretamente, mesmo que o hook useGrades
  // esteja sendo usado. O hook será atualizado automaticamente quando os filtros mudarem.
  try {
    // Extrair TurmaID corretamente - pode ser um array ou número
    let turmaId = classrooms[1];
    if (Array.isArray(turmaId)) {
      turmaId = turmaId[0];
    }
    turmaId = typeof turmaId === 'string' ? parseInt(turmaId, 10) : Number(turmaId);
    
    if (turmaId && !isNaN(turmaId) && subject?.id && period) {
      // Sempre usar getGradesByClassroom diretamente (como na versão antiga que funcionava)
      // Isso garante que os dados sejam atualizados imediatamente, sem depender do hook
      const updatedGrades = await getGradesByClassroom(
        turmaId,
        subject.id,
        period
      );
      
      // Atualizar classGrades diretamente (como na versão antiga)
      setClassGrades(updatedGrades);
      
      // Se refetchGrades estiver disponível, também chamar para sincronizar o hook
      // Mas não esperar por ele, pois já atualizamos classGrades diretamente
      if (refetchGrades && typeof refetchGrades === 'function') {
        refetchGrades().catch(() => {});
      }
    }
  } catch (error) {
    console.error(
      "Erro ao recarregar notas após atualização de visibilidade:",
      error
    );
    // Não falhar completamente se o recarregamento der erro
    // As notas podem ter sido atualizadas mesmo que o recarregamento falhe
  }

  setLoading(false);
  setInfoMessage(
    `Visibilidade atualizada: ${successCount} sucesso, ${errorCount} falhas`
  );
  setShowMessage(true);
  setUpdateStudentVisibility((prev) => prev + 1);
};


